import React, { useState, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect, useDispatch } from 'react-redux'
import { Link } from 'react-router'
import { selectors as apiSelectors } from 'lp-redux-api'
import { selectors as globalSchoolSelectors } from 'school-portal-reducer'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors as surveySelectors } from '../../survey/reducer'
import { selectors } from '../reducer'
import * as Types from 'types'
import { PartnershipTableForm } from '../../survey/forms'
import { PartnershipQuestionForm } from 'forms'
import * as apiActions from 'api-actions'
import * as actions from '../actions'
import { Spinner, Button, ButtonArea } from 'lp-components'
import { SchoolYearTabBar, ExternalLink, ErrorLoadState } from 'components'
import {
  displaySubmitFailure,
  useCommunity,
  isTeachingArtistEnabled,
  useMultiFormSave,
} from 'utils'
import * as flashActions from 'redux-flash'
import { WHATS_HAPPENING_NOW_TEXT } from 'config'
import { sortBy, get } from 'lodash'
import Interests from '../components/interests'
import syncProfileCompletion from '../sync-profile-completion'
import { buildInterestCompletionOptions } from '../profile-completion-sections'

const propTypes = {
  activeSchoolYears: PropTypes.arrayOf(Types.schoolYear).isRequired,
  school: Types.school.isRequired,
  updateSchool: PropTypes.func.isRequired,
  programTypes: PropTypes.array.isRequired,
  partnerOptions: PropTypes.arrayOf(Types.searchSuggestion),
  artistOptions: PropTypes.arrayOf(Types.searchSuggestion),
  partnershipQuestion: Types.partnershipQuestion,
  surveySchoolYear: Types.schoolYear,
  isSurveyOpen: PropTypes.bool.isRequired,
  fetchPartnerOptions: PropTypes.func.isRequired,
  fetchPartnershipQuestion: PropTypes.func.isRequired,
  clearPartnershipQuestion: PropTypes.func.isRequired,
  isLoadingPartnershipQuestion: PropTypes.bool.isRequired,
  hasPartnershipQuestionLoadFailed: PropTypes.bool.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  fetchArtistOptions: PropTypes.func.isRequired,
  currentSchoolYear: Types.schoolYear.isRequired,
  interestCompletionOptions: PropTypes.object,
  fetchCompletionSchool: PropTypes.func.isRequired,
  profileCompletionSchoolYear: Types.schoolYear,
}

const defaultProps = {
  partnerOptions: null,
  surveySchoolYear: null,
  artistOptions: null,
  interestCompletionOptions: {},
  profileCompletionSchoolYear: null,
}

function SurveyRedirectNote({ surveySchoolYearNumber }) {
  return (
    <section className="card">
      <p>
        Please list this year's partnerships in the{' '}
        <Link href="/school-portal/survey/community-and-partnerships">
          Community and Partnerships
        </Link>{' '}
        section of the{' '}
        <Link href="/school-portal/survey/">
          {surveySchoolYearNumber} artlook survey
        </Link>
        .
      </p>
    </section>
  )
}

function CustomPartnershipQuestionHeader({
  surveySchoolYearNumber,
  isSurveyOpen,
  supportEmail,
}) {
  return (
    <React.Fragment>
      <h2>
        Which arts organizations / teaching artists is your school partnering
        with this year?
      </h2>
      <p>
        In this section, please list only partnerships that are happening during
        this School Year.
      </p>
      {isSurveyOpen && surveySchoolYearNumber && (
        <p>
          You can list partnerships that happened in your prior School Year in
          your <strong>{surveySchoolYearNumber}</strong> Survey.
        </p>
      )}
      <p>
        Please contact{' '}
        <ExternalLink href={`mailto:${supportEmail}`}>
          {supportEmail}
        </ExternalLink>{' '}
        if you have any questions.
      </p>
    </React.Fragment>
  )
}

function PartnershipsWithInterests({
  activeSchoolYears,
  school,
  updateSchool,
  programTypes,
  partnerOptions,
  artistOptions,
  partnershipQuestion,
  surveySchoolYear,
  isSurveyOpen,
  fetchPartnerOptions,
  fetchArtistOptions,
  fetchPartnershipQuestion,
  clearPartnershipQuestion,
  isLoadingPartnershipQuestion,
  currentSchoolYear,
  hasPartnershipQuestionLoadFailed,
  flashSuccessMessage,
  interestCompletionOptions,
  fetchCompletionSchool,
  profileCompletionSchoolYear,
}) {
  useEffect(() => {
    if (!partnerOptions) fetchPartnerOptions()
  }, [partnerOptions])

  useEffect(() => {
    if (!artistOptions) fetchArtistOptions()
  }, [artistOptions])

  useEffect(() => {
    fetchPartnershipQuestion()

    return () => clearPartnershipQuestion()
  }, [])

  const { partnerships = [], openToPartnerships } = school

  const currentSchoolYearId = currentSchoolYear ? currentSchoolYear.id : null
  const [currentSchoolYearTab, setCurrentSchoolYearTab] =
    useState(currentSchoolYearId)

  const partnershipsForSchoolYear = useMemo(() => {
    const filteredPartnerships = partnerships.filter(
      ({ schoolYearId }) => schoolYearId === currentSchoolYearTab
    )
    return sortBy(filteredPartnerships, 'organizationName')
  }, [partnerships, currentSchoolYearTab])

  const previousPartnerships = useMemo(
    () =>
      partnerships.filter(
        ({ schoolYearId }) => schoolYearId !== currentSchoolYearId
      ),
    [partnerships, currentSchoolYearId]
  )
  const updateSchoolOnSubmit = useCallback(
    (params) => updateSchool(school.id, currentSchoolYearId, params),
    [school, currentSchoolYearId]
  )
  const onSuccess = useCallback(
    (updatedSchool) => {
      syncProfileCompletion({
        school: updatedSchool,
        previousSchool: school,
        updateSchoolBadge: updateSchoolOnSubmit,
        options: interestCompletionOptions,
      })
      // Keep the current-year completion school in sync so the completion bar
      // reflects this save immediately.
      if (profileCompletionSchoolYear) {
        fetchCompletionSchool(school.id, profileCompletionSchoolYear.id)
      }
      flashSuccessMessage(Types.SCHOOL_SUCCESS_MESSAGE)
    },
    [
      flashSuccessMessage,
      school,
      updateSchoolOnSubmit,
      interestCompletionOptions,
      fetchCompletionSchool,
      profileCompletionSchoolYear,
    ]
  )

  const community = useCommunity()

  // Single page-level Save orchestrates the Interests and current-year
  // partnership table forms. The "Looking for Partnerships?" toggle keeps its
  // existing autosave-on-change behavior and is intentionally excluded here.
  const { save, saving, formCallbacks } = useMultiFormSave()
  const dispatch = useDispatch()
  const handleSave = useCallback(() => {
    save({
      formNames: ['school-interests', 'school-partnership-table'],
      onComplete: (updatedSchool) => onSuccess(updatedSchool || school),
      onError: ({ errors, submitError } = {}) =>
        displaySubmitFailure(errors, dispatch, submitError),
    })
  }, [save, onSuccess, school, dispatch])

  if (
    !partnerOptions ||
    !artistOptions ||
    !currentSchoolYear ||
    isLoadingPartnershipQuestion
  )
    return <Spinner />

  return (
    <div>
      <br />
      <p>{WHATS_HAPPENING_NOW_TEXT}</p>
      {partnershipQuestion && (
        <section className="card">
          <PartnershipQuestionForm
            partnershipQuestion={partnershipQuestion}
            initialValues={{ openToPartnerships }}
            onSubmit={updateSchoolOnSubmit}
            onSubmitSuccess={onSuccess}
            onSubmitFail={displaySubmitFailure}
          />
        </section>
      )}
      {hasPartnershipQuestionLoadFailed && (
        <ErrorLoadState
          componentName="Looking for Partnerships form"
          headerAs="h2"
        />
      )}

      <Interests
        expandForm={openToPartnerships}
        hideSubmit
        {...formCallbacks}
      />

      {/* Capture the current year's partnership _only if_ that year differs from the survey,
      otherwise direct user to survey */}
      {surveySchoolYear && surveySchoolYear.id === currentSchoolYearId ? (
        <SurveyRedirectNote surveySchoolYearNumber={surveySchoolYear.number} />
      ) : (
        <div className="partnerships-year-select">
          <SchoolYearTabBar
            schoolYears={activeSchoolYears}
            value={currentSchoolYearTab}
            onChange={setCurrentSchoolYearTab}
          />
          <PartnershipTableForm
            hideSubmit
            initialValues={{
              partnerships: partnershipsForSchoolYear,
            }}
            onSubmit={updateSchoolOnSubmit}
            {...formCallbacks}
            visibleSchoolYearId={currentSchoolYearTab}
            programTypes={programTypes}
            schoolYears={activeSchoolYears}
            partnershipOptions={
              isTeachingArtistEnabled(community)
                ? [...(partnerOptions || []), ...(artistOptions || [])]
                : [...(partnerOptions || [])]
            }
            otherPartnerships={previousPartnerships}
            disabled={currentSchoolYearTab !== currentSchoolYearId}
            surveySchoolYearNumber={get(surveySchoolYear, 'number')}
            headerComponent={({ surveySchoolYearNumber }) => (
              <CustomPartnershipQuestionHeader
                isSurveyOpen={isSurveyOpen}
                surveySchoolYearNumber={surveySchoolYearNumber}
                supportEmail={community.schoolSupportEmail}
              />
            )}
          />
        </div>
      )}
      <ButtonArea>
        <Button
          style="primary"
          submitting={saving}
          onClick={handleSave}
          data-cy="save-partnerships-with-interests"
        >
          Save
        </Button>
      </ButtonArea>
    </div>
  )
}

PartnershipsWithInterests.propTypes = propTypes
PartnershipsWithInterests.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    activeSchoolYears: globalSchoolSelectors.orderedActiveSchoolYears(state),
    school: globalSchoolSelectors.school(state),
    programTypes: globalSelectors.orderedProgramTypes(state),
    partnerOptions: globalSchoolSelectors.partnerOptions(state),
    artistOptions: globalSchoolSelectors.artistOptions(state),
    partnershipQuestion: selectors.partnershipQuestion(state),
    isSurveyOpen: surveySelectors.isSurveyOpen(state),
    surveySchoolYear: globalSchoolSelectors.surveySchoolYear(state),
    currentSchoolYear: globalSchoolSelectors.currentSchoolYear(state),
    profileCompletionSchoolYear: globalSelectors.currentSchoolYear(state),
    isLoadingPartnershipQuestion: apiSelectors.isLoading(
      state,
      apiActions.fetchPartnershipQuestion
    ),
    hasPartnershipQuestionLoadFailed: apiSelectors.isFailure(
      state,
      apiActions.fetchPartnershipQuestion
    ),
    interestCompletionOptions: buildInterestCompletionOptions(state),
  }
}

const mapDispatchToProps = {
  flashSuccessMessage: flashActions.flashSuccessMessage,
  fetchPartnerOptions: apiActions.fetchPartnerOptions,
  fetchArtistOptions: apiActions.fetchArtistOptions,
  fetchPartnershipQuestion: apiActions.fetchPartnershipQuestion,
  clearPartnershipQuestion: actions.clearPartnershipQuestion,
  updateSchool: apiActions.updateSchool,
  fetchCompletionSchool: apiActions.fetchCompletionSchool,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  PartnershipsWithInterests
)
